import React, { useEffect, useState, useCallback } from "react";
import { LifeBuoy, Loader2, Send, Plus, ChevronLeft } from "lucide-react";
import { PageHeader } from "@/components/shell/TopBar";
import { listTickets, createTicket, listTicketMessages, postTicketMessage, postLaunchEvent } from "@/api/launch";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

const CATS: [string, string, string][] = [
  ["payment", "Ödeme", "Payment"], ["membership", "Üyelik", "Membership"], ["technical", "Teknik", "Technical"],
  ["signal", "Sinyal", "Signal"], ["atlas", "ATLAS", "ATLAS"], ["other", "Diğer", "Other"],
];
const S = (v: unknown) => (v == null ? "" : String(v));
const arr = (d: any): any[] => (Array.isArray(d) ? d : d?.tickets ?? d?.items ?? d?.messages ?? d?.data ?? []);

export default function Support() {
  const { lang } = useI18n();
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ category: "payment", subject: "", message: "" });
  const [selected, setSelected] = useState<any | null>(null);
  const [thread, setThread] = useState<any[]>([]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    listTickets().then((d) => setTickets(arr(d))).catch(() => setTickets([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { postLaunchEvent("support_opened"); load(); }, [load]);

  const openTicket = (t: any) => {
    setSelected(t); setThread([]);
    const id = S(t?.id ?? t?.ticket_id);
    if (id) listTicketMessages(id).then((d) => setThread(arr(d))).catch(() => setThread([]));
  };

  async function submitNew(e: React.FormEvent) {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) return;
    setBusy(true);
    try { await createTicket(form); toast.success(lang === "tr" ? "Talebin oluşturuldu" : "Ticket created"); setCreating(false); setForm({ category: "payment", subject: "", message: "" }); load(); }
    catch { toast.error(lang === "tr" ? "Talep oluşturulamadı" : "Could not create ticket"); }
    finally { setBusy(false); }
  }
  async function sendReply() {
    const id = S(selected?.id ?? selected?.ticket_id);
    if (!id || !reply.trim()) return;
    setBusy(true);
    try { await postTicketMessage(id, reply); setReply(""); const d = await listTicketMessages(id); setThread(arr(d)); }
    catch { toast.error(lang === "tr" ? "Mesaj gönderilemedi" : "Could not send message"); }
    finally { setBusy(false); }
  }

  if (selected) {
    return (
      <div data-testid="support-detail">
        <button onClick={() => setSelected(null)} className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ChevronLeft className="h-4 w-4" /> {lang === "tr" ? "Taleplere dön" : "Back to tickets"}</button>
        <h2 className="text-xl font-bold text-foreground">{S(selected?.subject) || (lang === "tr" ? "Talep" : "Ticket")}</h2>
        <p className="mt-1 text-xs text-muted-foreground">{S(selected?.status)}</p>
        <div className="panel mt-4 space-y-3 p-4">
          {thread.length === 0 ? <p className="text-sm text-muted-foreground">{lang === "tr" ? "Henüz mesaj yok." : "No messages yet."}</p>
            : thread.map((m, i) => (
              <div key={i} className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${S(m?.sender ?? m?.author ?? m?.role).toLowerCase().includes("admin") || S(m?.from).toLowerCase().includes("support") ? "mr-auto border border-white/8 bg-white/[0.03] text-foreground/90" : "ml-auto bg-atlas/15 text-foreground"}`}>
                {S(m?.message ?? m?.body ?? m?.text)}
              </div>
            ))}
        </div>
        <div className="mt-3 flex gap-2">
          <input value={reply} onChange={(e) => setReply(e.target.value)} data-testid="support-reply-input" placeholder={lang === "tr" ? "Mesajını yaz…" : "Write a message…"} className="flex-1 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-foreground outline-none focus:border-atlas/40" />
          <button onClick={sendReply} disabled={busy} data-testid="support-reply-send" className="inline-flex items-center gap-1.5 rounded-xl bg-atlas-500 px-4 py-2.5 text-sm font-bold text-atlas-ink">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="support-page">
      <PageHeader title={lang === "tr" ? "Yardım & Destek" : "Help & Support"}
        subtitle={lang === "tr" ? "Taleplerini oluştur ve takip et." : "Create and track your requests."} />
      <div className="mb-5 flex justify-end">
        <button onClick={() => setCreating((v) => !v)} data-testid="new-ticket-btn" className="inline-flex items-center gap-2 rounded-full bg-atlas-500 px-4 py-2 text-sm font-bold text-atlas-ink"><Plus className="h-4 w-4" /> {lang === "tr" ? "Yeni Talep" : "New Ticket"}</button>
      </div>

      {creating && (
        <form onSubmit={submitNew} className="panel mb-6 space-y-3 p-5" data-testid="new-ticket-form">
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="ticket-category" className="w-full rounded-xl border border-white/10 bg-navy-900 px-4 py-2.5 text-sm text-foreground outline-none">
            {CATS.map(([v, tr, en]) => <option key={v} value={v}>{lang === "tr" ? tr : en}</option>)}
          </select>
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="ticket-subject" placeholder={lang === "tr" ? "Konu" : "Subject"} className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-foreground outline-none focus:border-atlas/40" />
          <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="ticket-message" rows={4} placeholder={lang === "tr" ? "Mesajın" : "Your message"} className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-foreground outline-none focus:border-atlas/40" />
          <button type="submit" disabled={busy} data-testid="ticket-submit" className="inline-flex items-center gap-2 rounded-full bg-atlas-500 px-5 py-2.5 text-sm font-bold text-atlas-ink">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {lang === "tr" ? "Gönder" : "Submit"}</button>
        </form>
      )}

      {loading ? <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-atlas-400" /></div>
        : tickets.length === 0 ? (
          <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-8 text-center text-sm text-muted-foreground" data-testid="support-empty">
            <LifeBuoy className="mx-auto mb-3 h-6 w-6 text-muted-foreground" />
            {lang === "tr" ? "Henüz bir destek talebin yok." : "You don't have any support tickets yet."}
          </div>
        ) : (
          <div className="space-y-2" data-testid="ticket-list">
            {tickets.map((t, i) => (
              <button key={t?.id ?? i} onClick={() => openTicket(t)} data-testid={`ticket-row-${i}`} className="panel flex w-full items-center justify-between gap-3 p-4 text-left transition hover:border-atlas/30">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground">{S(t?.subject) || (lang === "tr" ? "Talep" : "Ticket")}</p>
                  <p className="truncate text-xs text-muted-foreground">{S(t?.category)}</p>
                </div>
                <span className="shrink-0 rounded-full border border-white/10 px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground">{S(t?.status) || "—"}</span>
              </button>
            ))}
          </div>
        )}
    </div>
  );
}
